/* Authoring a definition, and publishing the next tolerance version. Validation only — no state.
 *
 * **Both were deliberately kept out of the UI once, and the reasons are what this file answers.**
 * A definition names two tables, their key columns and a per-field tolerance list an engineer
 * verifies against their own schema; getting a key column wrong is precisely the input that produces
 * the `keys_disjoint` refusal. And a tolerance version is immutable with every past run citing it,
 * so a mistyped bound is permanent history rather than an edit somebody can undo.
 *
 * So the wizard's job is not collecting five fields. It is making somebody confident about the two
 * key-column lists before they submit, and making the permanence of a publish felt at the moment of
 * publishing. Everything below exists to serve one of those two.
 */

import type {
  Definition,
  FieldTolerance,
  ToleranceKind,
  ToleranceVersion,
} from '@/shared/api/generated/reconciliation';

/* ------------------------------------------------------------------ the tolerance bounds */

/** What a kind's bound means, and therefore how it is validated and labelled.
 *
 * The three numeric kinds have genuinely different ranges and the substrate enforces each: a
 * `relative` bound at or above 1 is refused precisely so a 5 typed for 5% is a refusal rather than a
 * silent hundredfold widening, and `fuzzy` admits exactly 1 because a similarity threshold of 1 is
 * "identical", which is a thing somebody may legitimately ask for.
 */
export interface BoundRule {
  readonly required: boolean;
  readonly labelKey: string;
  readonly noteKey: string;
  readonly placeholder: string;
}

export const BOUND_RULES = {
  exact: {
    required: false,
    labelKey: 'reconciliation.bound.label.none',
    noteKey: 'reconciliation.kind.note.exact',
    placeholder: '',
  },
  absolute: {
    required: true,
    labelKey: 'reconciliation.bound.label.absolute',
    noteKey: 'reconciliation.kind.note.absolute',
    placeholder: '0.01',
  },
  relative: {
    required: true,
    labelKey: 'reconciliation.bound.label.relative',
    noteKey: 'reconciliation.kind.note.relative',
    placeholder: '0.001',
  },
  fuzzy: {
    required: true,
    labelKey: 'reconciliation.bound.label.fuzzy',
    noteKey: 'reconciliation.kind.note.fuzzy',
    placeholder: '0.9',
  },
} satisfies Record<ToleranceKind, BoundRule>;

export const TOLERANCE_KINDS: readonly ToleranceKind[] = [
  'exact',
  'absolute',
  'relative',
  'fuzzy',
];

/** What is wrong with one term's bound, or `null`.
 *
 * An empty bound on a kind that needs one is a *problem* rather than a zero, which is the same
 * absence-is-not-zero rule the rest of this feature holds: submitting 0 for an unfilled absolute
 * tolerance would declare that the two sides must agree exactly, which is a different rule from the
 * one nobody finished writing.
 */
export function boundProblemKey(kind: ToleranceKind, raw: string): string | null {
  const rule = BOUND_RULES[kind];
  if (!rule.required) return null;

  const trimmed = raw.trim();
  if (trimmed === '') return 'reconciliation.bound.error.missing';

  const value = Number(trimmed);
  if (!Number.isFinite(value)) return 'reconciliation.bound.error.number';
  if (value <= 0) return 'reconciliation.bound.error.positive';
  if (kind === 'relative' && value >= 1) return 'reconciliation.bound.error.ratio';
  if (kind === 'fuzzy' && value > 1) return 'reconciliation.bound.error.similarity';
  return null;
}

export interface TermDraft {
  readonly leftColumn: string;
  readonly rightColumn: string;
  readonly kind: ToleranceKind;
  readonly bound: string;
}

export function termProblemKey(term: TermDraft): string | null {
  if (term.leftColumn.trim() === '' || term.rightColumn.trim() === '') {
    return 'reconciliation.term.error.columns';
  }
  return boundProblemKey(term.kind, term.bound);
}

/** A draft term as the API's own shape. `exact` sends **no** bound rather than a null one, because
 * the contract says it is absent for that kind. */
export function termToField(term: TermDraft): FieldTolerance {
  const base = {
    left_column: term.leftColumn.trim(),
    right_column: term.rightColumn.trim(),
    kind: term.kind,
  };
  return BOUND_RULES[term.kind].required
    ? { ...base, bound: Number(term.bound.trim()) }
    : base;
}

/* ------------------------------------------------------------------ the definition wizard */

export interface DefinitionDraft {
  readonly name: string;
  readonly connectionId: string;
  readonly left: string;
  readonly leftKeys: readonly string[];
  readonly right: string;
  readonly rightKeys: readonly string[];
  readonly terms: readonly TermDraft[];
}

export const EMPTY_TERM: TermDraft = {
  leftColumn: '',
  rightColumn: '',
  kind: 'exact',
  bound: '',
};

export const EMPTY_DRAFT: DefinitionDraft = {
  name: '',
  connectionId: '',
  left: '',
  leftKeys: [''],
  right: '',
  rightKeys: [''],
  terms: [EMPTY_TERM],
};

/** Whether a name collides with one already on this tenant.
 *
 * **Compared without case or spacing**, because that is what the server does — so a form that
 * checked a plain equality would let somebody submit "Repayments : Ledger" against an existing
 * "repayments: ledger" and meet a 409 they had no warning of.
 */
export function isDuplicateName(name: string, existing: readonly Definition[]): boolean {
  const normalise = (value: string): string => value.toLowerCase().replace(/\s+/gu, '');
  const candidate = normalise(name);
  if (candidate === '') return false;
  return existing.some((definition) => normalise(definition.name) === candidate);
}

/** The key columns actually entered, blanks dropped. The form always carries one empty row so there
 * is something to type into; an empty row is not a key column. */
export function filledKeys(keys: readonly string[]): readonly string[] {
  return keys.map((key) => key.trim()).filter((key) => key !== '');
}

/** Whether the two key lists can be paired at all.
 *
 * **Positional, which is the whole of step 1.** Row 1 of the left pairs with row 1 of the right, so
 * uneven counts is not a formatting complaint — it is exactly the shape of a wrong pairing, and it
 * is the thing that produces `keys_disjoint` if it reaches the server.
 */
export function keyCountMismatch(draft: DefinitionDraft): boolean {
  const left = filledKeys(draft.leftKeys).length;
  const right = filledKeys(draft.rightKeys).length;
  return left > 0 && right > 0 && left !== right;
}

/** What is still missing on step 1, or `null` when it may be left. */
export function step1ProblemKey(
  draft: DefinitionDraft,
  existing: readonly Definition[],
): string | null {
  if (draft.connectionId === '') return 'reconciliation.wizard.error.connection';
  if (draft.left === '' || draft.right === '')
    return 'reconciliation.wizard.error.datasets';
  if (
    filledKeys(draft.leftKeys).length === 0 ||
    filledKeys(draft.rightKeys).length === 0
  ) {
    return 'reconciliation.wizard.error.keys';
  }
  if (keyCountMismatch(draft)) return 'reconciliation.wizard.error.keyCount';
  if (draft.name.trim() === '') return 'reconciliation.wizard.error.name';
  if (isDuplicateName(draft.name, existing))
    return 'reconciliation.wizard.error.duplicate';
  return null;
}

/** What is still wrong on step 2, or `null`. At least one tolerance, and every one of them valid —
 * a definition cannot run without one, and the API refuses an empty list. */
export function step2ProblemKey(draft: DefinitionDraft): string | null {
  if (draft.terms.length === 0) return 'reconciliation.wizard.error.noTerms';
  for (const term of draft.terms) {
    const problem = termProblemKey(term);
    if (problem !== null) return problem;
  }
  return null;
}

/** The draft as the API's `DefinitionWrite`. Only ever called once both steps report no problem. */
export function draftToWrite(draft: DefinitionDraft): {
  readonly name: string;
  readonly connection_id: string;
  readonly left: {
    readonly qualified_name: string;
    readonly key_columns: readonly string[];
  };
  readonly right: {
    readonly qualified_name: string;
    readonly key_columns: readonly string[];
  };
  readonly tolerances: readonly FieldTolerance[];
} {
  return {
    name: draft.name.trim(),
    connection_id: draft.connectionId,
    left: { qualified_name: draft.left, key_columns: filledKeys(draft.leftKeys) },
    right: { qualified_name: draft.right, key_columns: filledKeys(draft.rightKeys) },
    tolerances: draft.terms.map(termToField),
  };
}

/* ------------------------------------------------------------------ publishing a version */

export type ChangeKind = 'unchanged' | 'changed';

/** One row of the publish diff: the pair, what it was, what it becomes.
 *
 * **A diff rather than a fresh form**, because a retune is almost always a change to one bound in an
 * otherwise settled list — and a blank form would make somebody retype the bounds they are keeping,
 * which is how a bound gets mistyped on the one screen where a mistype is permanent.
 */
export interface DiffRow {
  readonly pair: string;
  readonly kind: ToleranceKind;
  readonly previous: number | null;
  readonly next: string;
  readonly change: ChangeKind;
}

export function diffRows(
  current: ToleranceVersion,
  bounds: Readonly<Record<string, string>>,
): readonly DiffRow[] {
  return current.fields.map((field) => {
    const pair = `${field.left_column} → ${field.right_column}`;
    const previous = field.bound ?? null;
    const next = bounds[pair] ?? (previous === null ? '' : String(previous));
    const changed =
      BOUND_RULES[field.kind].required && next.trim() !== String(previous ?? '');
    return {
      pair,
      kind: field.kind,
      previous,
      next,
      change: changed ? 'changed' : 'unchanged',
    };
  });
}

/** The version number a publish will create. One past the version in force — the API appends and
 * never edits, so this is arithmetic rather than a guess. */
export function nextVersion(current: ToleranceVersion): number {
  return current.version + 1;
}

/** The diff as tolerances to send, or the first problem found. */
export function diffToTolerances(
  rows: readonly DiffRow[],
): { readonly tolerances: readonly FieldTolerance[] } | { readonly problemKey: string } {
  const tolerances: FieldTolerance[] = [];
  for (const row of rows) {
    const [left = '', right = ''] = row.pair.split(' → ');
    const problem = boundProblemKey(row.kind, row.next);
    if (problem !== null) return { problemKey: problem };
    tolerances.push(
      termToField({
        leftColumn: left,
        rightColumn: right,
        kind: row.kind,
        bound: row.next,
      }),
    );
  }
  return { tolerances };
}
